import { useState, useEffect, useRef } from "react"
import { OnboardingStep } from "@/hooks/use-onboarding"
import { isPersonPrefillEnabled } from "@/constants"
import { CvUploadLinkedInSlot, type PersonLinkedInDraftStatus } from "@/components/onboarding/person-linkedin-paste"
import {
  draftPersonFromLinkedIn,
  readPersonPrefillDraft,
  storePersonPrefillDraft,
} from "@/lib/person-prefill"
import {
  useUploadCV,
  useCVDocument,
  useCVDocuments,
  useRetryCVParse,
  useDeleteCVDocument,
} from "@/hooks/use-cv-upload"
import { useProfile } from "@/hooks/use-profile"
import { OnboardingStepActions } from '@/components/onboarding/onboarding-step-actions'
import { Button } from "@/components/ui/button"
import { FileUpload } from "@/components/ui/file-upload"
import { CVParsingStatus } from "@/components/ui/cv-parsing-status"
import { Skeleton } from "@/components/ui/skeleton"
import { useQueryClient } from "@tanstack/react-query"
import { getPrimaryCvDocument } from "@/lib/cv-display"
import { OnboardingStepNext, runOnboardingStepNext } from "@/lib/onboarding-step-nav"
import { cn } from "@/lib/utils"

interface CVUploadStepProps {
  step: OnboardingStep
  onNext: OnboardingStepNext
  onBack?: () => void
  isCompleting: boolean
  canGoBack: boolean
}

export function CVUploadStep({ onNext, onBack, isCompleting, canGoBack }: CVUploadStepProps) {
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uploadedCVId, setUploadedCVId] = useState<string | null>(null)
  const [replacing, setReplacing] = useState(false)
  const { uploadCV } = useUploadCV()
  const { retryCVParse, isRetrying } = useRetryCVParse()
  const { deleteCV, isDeleting } = useDeleteCVDocument()
  const { cvDocuments, isLoading: cvListLoading, refetch: refetchCvDocuments } = useCVDocuments()
  const queryClient = useQueryClient()
  const { refetch: refetchProfile } = useProfile()
  const prefillEnabled = isPersonPrefillEnabled()
  const [linkedinUrl, setLinkedinUrl] = useState("")
  const [draftStatus, setDraftStatus] = useState<PersonLinkedInDraftStatus>("idle")
  const [draftError, setDraftError] = useState<string | null>(null)
  const draftAbortRef = useRef<AbortController | null>(null)

  useEffect(() => () => draftAbortRef.current?.abort(), [])

  useEffect(() => {
    if (!prefillEnabled) return
    const cached = readPersonPrefillDraft(queryClient)
    if (cached?.written === false) {
      setDraftStatus("drafted")
      if (cached.linkedin_url) setLinkedinUrl(cached.linkedin_url)
    }
  }, [prefillEnabled, queryClient])

  const handleDraftLinkedIn = async () => {
    draftAbortRef.current?.abort()
    const controller = new AbortController()
    draftAbortRef.current = controller
    setDraftStatus("drafting")
    setDraftError(null)
    try {
      const result = await draftPersonFromLinkedIn(linkedinUrl, controller.signal)
      if (controller.signal.aborted) return
      storePersonPrefillDraft(queryClient, result)
      setDraftStatus("drafted")
    } catch (err) {
      if (controller.signal.aborted) return
      console.error("LinkedIn draft failed:", err)
      setDraftStatus("failed")
      setDraftError(err instanceof Error ? err.message : "Couldn't draft from that LinkedIn link.")
    }
  }

  const linkedInPaste = (
    <CvUploadLinkedInSlot
      enabled={prefillEnabled}
      linkedinUrl={linkedinUrl}
      onLinkedinUrlChange={setLinkedinUrl}
      status={draftStatus}
      error={draftError}
      disabled={isCompleting}
      onDraft={() => void handleDraftLinkedIn()}
    />
  )

  useEffect(() => {
    void refetchCvDocuments()
  }, [refetchCvDocuments])

  useEffect(() => {
    if (cvListLoading || replacing) return

    const primary = getPrimaryCvDocument(cvDocuments)
    if (primary) {
      setUploadedCVId(primary.id)
      return
    }

    if (uploadedCVId) {
      queryClient.removeQueries({ queryKey: ['cv-document', uploadedCVId] })
      setUploadedCVId(null)
    }
  }, [cvDocuments, cvListLoading, replacing, uploadedCVId, queryClient])

  const { cvDocument, error: cvDocumentError } = useCVDocument(uploadedCVId)

  useEffect(() => {
    if (!uploadedCVId || cvDocumentError?.message !== 'CV document not found') return

    queryClient.removeQueries({ queryKey: ['cv-document', uploadedCVId] })
    setUploadedCVId(null)
    void refetchCvDocuments()
  }, [cvDocumentError, uploadedCVId, queryClient, refetchCvDocuments])

  const handleFilesSelected = (files: File[]) => {
    const selectedFile = files[0]
    if (selectedFile) {
      setFile(selectedFile)
      setError(null)
    }
  }

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file')
      return
    }

    setUploading(true)
    setError(null)

    try {
      const result = await uploadCV(file)
      if (result.success && result.cv_document) {
        setReplacing(false)
        setUploadedCVId(result.cv_document.id)
        setFile(null)
      } else {
        setError(result.error || 'Failed to upload CV')
      }
    } catch (err) {
      console.error('CV upload error:', err)
      setError(err instanceof Error ? err.message : 'Network error. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const handleReplace = () => {
    setReplacing(true)
    setUploadedCVId(null)
    setFile(null)
    setError(null)
    setUploading(false)
  }

  const handleRetry = async () => {
    if (!uploadedCVId) return

    setError(null)
    try {
      await retryCVParse(uploadedCVId)
    } catch (err) {
      console.error('CV retry error:', err)
      setError(err instanceof Error ? err.message : 'Failed to retry. Please try again.')
    }
  }

  const handleCancel = async () => {
    if (!uploadedCVId) return

    const cvId = uploadedCVId
    setError(null)
    try {
      await deleteCV(cvId)
      queryClient.removeQueries({ queryKey: ['cv-document', cvId] })
      setUploadedCVId(null)
      setFile(null)
      setReplacing(false)
      setUploading(false)
      void refetchCvDocuments()
    } catch (err) {
      console.error('CV cancel error:', err)
      setError(err instanceof Error ? err.message : 'Failed to cancel. Please try again.')
    }
  }

  const handleContinue = async () => {
    queryClient.invalidateQueries({ queryKey: ['profile'] })
    void refetchProfile()
    await runOnboardingStepNext(onNext, setError)
  }

  const isProcessing = cvDocument && (cvDocument.parsing_status === 'pending' || cvDocument.parsing_status === 'processing')
  const isCompleted = cvDocument && cvDocument.parsing_status === 'completed'
  const isFailed = cvDocument && cvDocument.parsing_status === 'failed'
  const awaitingCvDetail =
    !!uploadedCVId &&
    !cvDocument &&
    cvDocumentError?.message !== 'CV document not found'
  const showUploadForm =
    !isProcessing && !isCompleted && !replacing && !isFailed && !awaitingCvDetail
  const showCompleted = isCompleted && !replacing
  const fileReady = !!file && !uploading

  if (cvListLoading && !uploadedCVId) {
    return (
      <div className="space-y-6">
        {linkedInPaste}
        <p className="text-sm text-muted-foreground">Checking for an existing CV…</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {linkedInPaste}
      <div className="space-y-4">
        {(isProcessing || showCompleted || isFailed) && cvDocument ? (
          <CVParsingStatus
            cvDocument={cvDocument}
            completedPresentation={showCompleted ? "file-summary" : "default"}
          />
        ) : null}

        {awaitingCvDetail ? (
          <div className="space-y-2" aria-busy="true" aria-label="Preparing your CV">
            <Skeleton className="h-[254px] w-full rounded-xl" />
            <p className="text-sm text-muted-foreground">Preparing your CV…</p>
          </div>
        ) : null}

        {showUploadForm && (
          <>
            <FileUpload
              accept={{
                'application/pdf': ['.pdf'],
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                'application/msword': ['.doc'],
              }}
              maxSize={10 * 1024 * 1024}
              multiple={false}
              onFilesSelected={handleFilesSelected}
              disabled={uploading || isCompleting}
              title={
                uploading
                  ? 'Uploading your CV…'
                  : fileReady
                    ? 'File ready — click Upload CV below'
                    : undefined
              }
              dropzoneCardClassName={cn(
                fileReady && 'ring-2 ring-primary ring-offset-2 animate-slow-pulse',
                uploading && 'ring-2 ring-primary/60 opacity-60',
              )}
              files={
                file
                  ? [{ file, status: uploading ? 'uploading' : 'pending' }]
                  : []
              }
            />
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
          </>
        )}
      </div>

      <OnboardingStepActions
        canGoBack={canGoBack}
        onBack={onBack}
        backDisabled={uploading || isCompleting || !!isProcessing || isRetrying || isDeleting}
        actions={
          showCompleted ? (
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleReplace} disabled={isCompleting}>
                Replace CV
              </Button>
              <Button onClick={handleContinue} disabled={isCompleting}>
                {isCompleting ? 'Continuing…' : 'Continue'}
              </Button>
            </div>
          ) : isFailed && cvDocument && !replacing ? (
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleReplace} disabled={isRetrying || isCompleting}>
                Upload new file
              </Button>
              <Button onClick={handleRetry} disabled={isRetrying || isCompleting || !uploadedCVId}>
                {isRetrying ? 'Retrying…' : 'Retry processing'}
              </Button>
            </div>
          ) : !isProcessing && !isCompleted ? (
            <Button onClick={handleUpload} disabled={!file || uploading || isCompleting}>
              {uploading ? 'Uploading...' : 'Upload CV'}
            </Button>
          ) : isProcessing ? (
            <div className="flex flex-col items-end gap-2">
              {error ? <p className="text-sm text-destructive">{error}</p> : null}
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isDeleting || isCompleting || !uploadedCVId}
              >
                {isDeleting ? 'Cancelling…' : 'Cancel'}
              </Button>
            </div>
          ) : null
        }
      />
    </div>
  )
}
